import axios from 'axios';
import { authService } from './authService';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add JWT token to requests
apiClient.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle 401 errors (token expired/invalid)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear it and redirect to login
      authService.clearToken();

      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/admin/login')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

export const api = {
  // Authentication endpoints
  login: async (password) => {
    const response = await apiClient.post('/auth/login', { password });
    return response.data;
  },

  // Availability endpoints
  getAvailability: async (params) => {
    const response = await apiClient.get('/availability', { params });
    return response.data;
  },

  // Booking endpoints
  createBooking: async (bookingData) => {
    const response = await apiClient.post('/bookings', bookingData);
    return response.data;
  },

  getBooking: async (bookingId) => {
    const response = await apiClient.get(`/bookings/${bookingId}`);
    return response.data;
  },

  getBookings: async (params) => {
    const response = await apiClient.get('/bookings', { params });
    return response.data;
  },

  cancelBooking: async (bookingId, token) => {
    const response = await apiClient.delete(`/bookings/${bookingId}`, {
      params: { token },
    });
    return response.data;
  },

  rescheduleBooking: async (bookingId, token) => {
    const response = await apiClient.post(`/bookings/${bookingId}/reschedule`, null, {
      params: { token },
    });
    return response.data;
  },

  techRescheduleBooking: async (bookingId, reason = null, integrationKey = null) => {
    const config = integrationKey ? { headers: { 'X-Integration-Key': integrationKey } } : {};
    const response = await apiClient.post(`/bookings/${bookingId}/tech-reschedule`, { reason }, config);
    return response.data;
  },

  techCancelBooking: async (bookingId, reason, integrationKey = null) => {
    const config = integrationKey ? { headers: { 'X-Integration-Key': integrationKey } } : {};
    const response = await apiClient.post(`/bookings/${bookingId}/tech-cancel`, { reason }, config);
    return response.data;
  },

  // Agent endpoints
  getAgents: async (activeOnly = true) => {
    const response = await apiClient.get('/agents', {
      params: { activeOnly },
    });
    return response.data;
  },

  createAgent: async (agentData) => {
    const response = await apiClient.post('/agents', agentData);
    return response.data;
  },

  updateAgent: async (agentId, updates) => {
    const response = await apiClient.put(`/agents/${agentId}`, updates);
    return response.data;
  },

  // Halo agent integration endpoints
  getHaloAgents: async () => {
    const response = await apiClient.get('/agents/halo');
    return response.data;
  },

  importHaloAgent: async (haloAgentId, timezone = 'America/New_York') => {
    const response = await apiClient.post('/agents/import', {
      haloAgentId,
      timezone,
    });
    return response.data;
  },

  getAgentsEnriched: async (activeOnly = true) => {
    const response = await apiClient.get('/agents', {
      params: { activeOnly, enrichFromHalo: true },
    });
    return response.data;
  },

  getAgentBookingLink: async (agentId) => {
    const response = await apiClient.get(`/agents/${agentId}/booking-link`);
    return response.data;
  },

  regenerateAgentBookingLink: async (agentId) => {
    const response = await apiClient.post(`/agents/${agentId}/booking-link/regenerate`);
    return response.data;
  },

  // Booking link endpoints
  generateLink: async (linkData, integrationKey = null) => {
    const config = integrationKey ? { headers: { 'X-Integration-Key': integrationKey } } : {};
    const response = await apiClient.post('/generate-link', linkData, config);
    return response.data;
  },

  getBookingLink: async (linkId) => {
    const response = await apiClient.get(`/booking-links/${linkId}`);
    return response.data;
  },

  getTicketLinks: async (ticketId, integrationKey = null) => {
    const config = integrationKey ? { headers: { 'X-Integration-Key': integrationKey } } : {};
    const response = await apiClient.get(`/tickets/${ticketId}/links`, config);
    return response.data;
  },

  // Form endpoints
  getForm: async (formToken) => {
    const response = await apiClient.get(`/forms/${formToken}`);
    return response.data;
  },

  submitForm: async (formToken, data) => {
    const response = await apiClient.post(`/forms/${formToken}/submit`, { data });
    return response.data;
  },

  generateFormLink: async (linkData, integrationKey = null) => {
    const config = integrationKey ? { headers: { 'X-Integration-Key': integrationKey } } : {};
    const response = await apiClient.post('/forms/generate-link', linkData, config);
    return response.data;
  },

  getTicketForms: async (ticketId, integrationKey = null) => {
    const config = integrationKey ? { headers: { 'X-Integration-Key': integrationKey } } : {};
    const response = await apiClient.get(`/tickets/${ticketId}/forms`, config);
    return response.data;
  },

  listFormDefinitions: async (integrationKey = null) => {
    const config = integrationKey ? { headers: { 'X-Integration-Key': integrationKey } } : {};
    const response = await apiClient.get('/form-definitions', config);
    return response.data;
  },

  createFormDefinition: async (formDefData) => {
    const response = await apiClient.post('/form-definitions', formDefData);
    return response.data;
  },

  updateFormDefinition: async (formDefId, updates) => {
    const response = await apiClient.put(`/form-definitions/${formDefId}`, updates);
    return response.data;
  },

  createPerpetualLink: async (formDefId) => {
    const response = await apiClient.post(`/form-definitions/${formDefId}/perpetual-link`);
    return response.data;
  },
};

export default api;
